import { accommodationOfferPageScopeKey } from '@luxuryescapes/contract-trip'
import { assertUnreachable } from 'lib/staticAssert/staticAssertUtils'
import { removeUndefinedAndNullProperties } from 'lib/object/objectUtils'
import type {
  AssistantSurfaceContextSnapshot,
  AssistantSurfaceId,
} from 'tripPlanner/assistant/api/types'

export interface AssistantTurnPolicySnapshot {
  currentSurface: AssistantSurfaceId
  regenerationScopeKey?: string
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function isAssistantSurfaceId(value: unknown): value is AssistantSurfaceId {
  switch (value) {
    case 'trip_planner':
    case 'accommodation_offer_page':
      return true
    default:
      return false
  }
}

export function buildAssistantTurnPolicySnapshot(
  surfaceContextSnapshot: AssistantSurfaceContextSnapshot,
): AssistantTurnPolicySnapshot {
  switch (surfaceContextSnapshot.surface) {
    case 'trip_planner':
      return {
        currentSurface: 'trip_planner',
        regenerationScopeKey: 'trip_planner',
      }
    case 'accommodation_offer_page': {
      const regenerationScopeKey = accommodationOfferPageScopeKey(
        removeUndefinedAndNullProperties(surfaceContextSnapshot.surfaceContext.pageIdentity),
      )

      return {
        currentSurface: 'accommodation_offer_page',
        ...(regenerationScopeKey ? { regenerationScopeKey } : {}),
      }
    }
    default:
      return assertUnreachable(surfaceContextSnapshot)
  }
}

export function buildAssistantTurnPolicySnapshotFromScope(
  currentSurface: AssistantSurfaceId,
  regenerationScopeKey: string | undefined,
): AssistantTurnPolicySnapshot {
  return {
    currentSurface,
    ...(regenerationScopeKey ? { regenerationScopeKey } : {}),
  }
}

export function isAssistantTurnPolicySnapshot(value: unknown): value is AssistantTurnPolicySnapshot {
  if (!isRecord(value)) {
    return false
  }

  const { currentSurface, regenerationScopeKey } = value
  return isAssistantSurfaceId(currentSurface) &&
    (regenerationScopeKey === undefined || typeof regenerationScopeKey === 'string')
}

export function isTripPlannerRegenerationCompatible(snapshot: unknown): boolean {
  if (snapshot === undefined) {
    return true
  }

  return isAssistantTurnPolicySnapshot(snapshot) && snapshot.currentSurface === 'trip_planner'
}

export function isOfferPageRegenerationCompatible(
  snapshot: unknown,
  currentRegenerationScopeKey: string | undefined,
): boolean {
  if (!currentRegenerationScopeKey || !isAssistantTurnPolicySnapshot(snapshot)) {
    return false
  }

  return snapshot.currentSurface === 'accommodation_offer_page' &&
    snapshot.regenerationScopeKey === currentRegenerationScopeKey
}
